/**
 * Form Validation Utilities
 *
 * Provides validation functions for common form fields
 * like email and phone numbers.
 */

/**
 * Validates email format
 * @param email - Email string to validate
 * @returns true if email format is valid
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || email.trim() === '') return true; // Empty is valid (optional field)
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email.trim());
};

/**
 * Validates phone number format
 * Allows digits, spaces, dashes, parentheses, plus sign
 * Requires 7-15 digits after cleaning
 * @param phone - Phone string to validate
 * @returns true if phone format is valid
 */
export const isValidPhone = (phone: string): boolean => {
  if (!phone || phone.trim() === '') return true; // Empty is valid (optional field)
  const cleaned = phone.replace(/[\s\-\(\)\+\.]/g, '');
  return /^\d{7,15}$/.test(cleaned);
};

/**
 * Formats phone number for display
 * @param phone - Phone string to format
 * @returns Formatted phone string
 */
export const formatPhone = (phone: string): string => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

/**
 * Validates URL format
 * @param url - URL string to validate
 * @returns true if URL format is valid
 */
export const isValidUrl = (url: string): boolean => {
  if (!url || url.trim() === '') return true; // Empty is valid (optional field)
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Validates a required field is not empty
 * @param value - String value to check
 * @returns true if value has content
 */
export const isNotEmpty = (value: string | undefined | null): boolean => {
  return value !== undefined && value !== null && value.trim() !== '';
};
